import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { track } from "@/lib/tracking";
import { api } from "@/lib/api";

const CartContext = createContext(null);
const STORAGE = "preshhgo_cart_v1";
const SESSION_KEY_STORAGE = "preshh_session_key";

function getSessionKey() {
  let k = localStorage.getItem(SESSION_KEY_STORAGE);
  if (!k) {
    k = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY_STORAGE, k);
  }
  return k;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState(() => localStorage.getItem("preshh_country") || "PT");
  const [referralCode, setReferralCode] = useState(() => localStorage.getItem("preshh_ref") || "");
  const [salesSuspended, setSalesSuspended] = useState(false);

  useEffect(() => {
    api.get("/storefront-config").then((r) => setSalesSuspended(!!r.data?.sales_suspended)).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE, JSON.stringify(items));
    // Heartbeat abandoned-cart tracker (debounced 3s)
    const t = setTimeout(() => {
      try {
        const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
        const token = localStorage.getItem("preshh_customer_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        api.post("/cart/heartbeat", {
          items: items.map((i) => ({ id: i.id, slug: i.slug, name: i.name, price: i.price, image: i.image, quantity: i.quantity })),
          subtotal,
          session_key: getSessionKey(),
        }, { headers }).catch(() => {});
      } catch { /* silent */ }
    }, 3000);
    return () => clearTimeout(t);
  }, [items]);

  useEffect(() => {
    localStorage.setItem("preshh_country", country);
  }, [country]);

  useEffect(() => {
    if (referralCode) localStorage.setItem("preshh_ref", referralCode);
    // Capture ?ref= from URL on load
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref && ref.startsWith("REF-")) {
      setReferralCode(ref.toUpperCase());
      localStorage.setItem("preshh_ref", ref.toUpperCase());
    }
    // eslint-disable-next-line
  }, []);

  const add = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((p) => p.id === product.id);
      if (existing) {
        return prev.map((p) => (p.id === product.id ? { ...p, quantity: p.quantity + qty } : p));
      }
      return [...prev, {
        id: product.id,
        slug: product.slug,
        name: product.name,
        price: product.price,
        image: (product.images || [])[0],
        quantity: qty,
      }];
    });
    track("add_to_cart", { item_id: product.id, item_name: product.name, price: product.price, quantity: qty, currency: product.currency || "EUR", value: product.price * qty });
    setOpen(true);
  }, []);

  const remove = useCallback((id) => {
    setItems((p) => {
      const it = p.find((i) => i.id === id);
      if (it) track("remove_from_cart", { item_id: id, item_name: it.name, quantity: it.quantity });
      return p.filter((i) => i.id !== id);
    });
  }, []);
  const setQty = useCallback((id, q) => setItems((p) => p.map((i) => i.id === id ? { ...i, quantity: Math.max(1, q) } : i)), []);
  const clear = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, open, setOpen, add, remove, setQty, clear, subtotal, count, country, setCountry, referralCode, setReferralCode, salesSuspended }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
